import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Button } from '@/components/form/baseInputs/Button';
import { TypeaheadMultiSelectInput } from '@/components/form/baseInputs/TypeaheadMultiSelectInput/TypeaheadMultiSelectInput';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { Alert } from '@/components/notifications/Alert/Alert';
import { ExistingDocumentPicker } from '@/modules/files/FileUploaderV2/ExistingDocumentPicker';
import { useAdminFormTestHouseholdsQuery } from './AdminFormTests.generated';
export interface TestSnapshot {
  id: string;
  version: number;
}
export interface FormTestCase {
  householdID: string;
  name: string;
  sourceDocumentIDs: string[];
  sourceDocumentNames?: string[];
}
export function canRunFormTest(cases: FormTestCase[]) {
  return cases.length > 0 && cases.length <= 10 && new Set(cases.map(item => item.householdID)).size === cases.length && cases.every(item => item.sourceDocumentIDs.length > 0);
}

/** Collects a valid test batch before the editor saves or creates a snapshot. */
export function AdminFormTestWorkspace(t0) {
  const $ = _c(57);
  const {
    initialCases: t1,
    isRunning,
    locked: t2,
    onClose,
    onRun
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const initialCases = t3;
  const locked = t2 === undefined ? false : t2;
  const [cases, setCases] = useState(initialCases);
  const [search, setSearch] = useState("");
  let t4;
  if ($[2] !== search) {
    t4 = {
      variables: {
        search
      }
    };
    $[2] = search;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const households = useAdminFormTestHouseholdsQuery(t4);
  let t5;
  if ($[4] !== isRunning || $[5] !== onClose) {
    t5 = () => {
      if (!isRunning) {
        onClose();
      }
    };
    $[4] = isRunning;
    $[5] = onClose;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isRunning || $[8] !== onClose) {
    t6 = <Button size="sm" variant="secondary" disabled={isRunning} onClick={onClose}>Cancel</Button>;
    $[7] = isRunning;
    $[8] = onClose;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== cases || $[11] !== isRunning) {
    t7 = isRunning || !canRunFormTest(cases);
    $[10] = cases;
    $[11] = isRunning;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== cases || $[14] !== onRun) {
    t8 = async () => onRun(cases);
    $[13] = cases;
    $[14] = onRun;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = locked ? "Retry same test" : "Run test";
  let t10;
  if ($[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t10 = <Button size="sm" variant="primary" disabled={t7} onClick={t8}>{t9}</Button>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t6) {
    t11 = <>{t6}{t10}</>;
    $[20] = t10;
    $[21] = t6;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== locked) {
    t12 = locked && <Alert severity="warning">The previous launch was not confirmed. Retry sends the same snapshot and document selection without creating duplicate cases.</Alert>;
    $[23] = locked;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== households.error) {
    t13 = households.error && <Alert severity="error">Households could not be loaded. Change the search to try again.</Alert>;
    $[25] = households.error;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = isRunning || locked || cases.length >= 10;
  let t16;
  if ($[28] !== households.data?.households.edges) {
    t16 = households.data?.households.edges ?? [];
    $[28] = households.data?.households.edges;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== cases || $[31] !== t16) {
    let t18;
    if ($[33] !== cases) {
      t18 = edge => edge?.node && !cases.some(item => item.householdID === edge.node?.id) ? [{
        value: edge.node.id,
        display: edge.node.name
      }] : [];
      $[33] = cases;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    t17 = t16.flatMap(t18);
    $[30] = cases;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = (_event, options) => {
      setCases(current => [...current, ...options.map(_temp)]);
      setSearch("");
    };
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== search || $[37] !== t15 || $[38] !== t17) {
    t19 = <TypeaheadMultiSelectInput label="Add household" values={t14} disabled={t15} inputValue={search} onInputChange={setSearch} options={t17} onChange={t18} />;
    $[36] = search;
    $[37] = t15;
    $[38] = t17;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== cases || $[41] !== isRunning || $[42] !== locked) {
    let t21;
    if ($[44] !== isRunning || $[45] !== locked) {
      t21 = item_0 => <Stack key={item_0.householdID} spacing={1}><Stack direction="row" justifyContent="space-between" alignItems="center"><Typography variant="title-xs">{item_0.name}</Typography><Button size="sm" variant="secondary" disabled={isRunning || locked} onClick={() => setCases(current_0 => current_0.filter(entry => entry.householdID !== item_0.householdID))}>Remove</Button></Stack>{locked ? <Typography variant="body-s">Selected documents:{" "}{(item_0.sourceDocumentNames ?? item_0.sourceDocumentIDs).join(", ")}</Typography> : <ExistingDocumentPicker householdId={item_0.householdID} value={item_0.sourceDocumentIDs} disabled={isRunning || locked} onChange={documents => setCases(current_1 => current_1.map(entry_0 => entry_0.householdID === item_0.householdID ? {
          ...entry_0,
          sourceDocumentIDs: documents.map(_temp2),
          sourceDocumentNames: documents.map(_temp3)
        } : entry_0))} />}</Stack>;
      $[44] = isRunning;
      $[45] = locked;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    t20 = cases.map(t21);
    $[40] = cases;
    $[41] = isRunning;
    $[42] = locked;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[47] !== t12 || $[48] !== t13 || $[49] !== t19 || $[50] !== t20) {
    t21 = <Stack spacing={2}>{t12}{t13}{t19}{t20}</Stack>;
    $[47] = t12;
    $[48] = t13;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== isRunning || $[53] !== t11 || $[54] !== t21 || $[55] !== t5) {
    t22 = <DialogModal isOpen={true} heading="New form test" subheading="Select up to 10 distinct households and at least one document for each household." onClose={t5} disableBackdropClick={isRunning} actions={t11}>{t21}</DialogModal>;
    $[52] = isRunning;
    $[53] = t11;
    $[54] = t21;
    $[55] = t5;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  return t22;
}
function _temp3(document_0) {
  return document_0.displayName;
}
function _temp2(document) {
  return document.id;
}
function _temp(option) {
  return {
    householdID: option.value,
    name: option.display,
    sourceDocumentIDs: []
  };
}